import { reactive, ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type {
  IncidentReportDetailsDto,
  AddressDto,
  BountyDto,
  Geometry
} from '@roadfootage/api-client'
import { AddressPrecision } from '@roadfootage/api-client'

import { useFileUploadStore } from '../file-upload'
import { useApi } from '../../composables/api'
import { regionConfig } from '@/config/region'
import { getExtractor } from '@/utils/vendors/registry'

export type StepName =
  | 'start'
  | 'vendor-browse'
  | 'media-upload'
  | 'incident-details'
  | 'refine-location'
  | 'review-submit'

export interface ExtractedMetadataHints {
  dateTime: boolean
  location: boolean
  description: boolean
}

export interface MetadataWarning {
  dateTime: boolean
  location: boolean
}

export interface LocalIncidentReportState extends IncidentReportDetailsDto {
  deviceVendor: string | null
  localLatitude: number | null
  localLongitude: number | null
  extractedMetadataHints: ExtractedMetadataHints
  metadataWarning: MetadataWarning
}

const initReportDetailsForm = (): LocalIncidentReportState => ({
  incidentReportType: undefined,
  primaryUserIncidentFileId: null,
  countryCode: regionConfig.countryCode,
  location: null,
  incidentDateTime: null,
  incidentTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  userSubmittedIncidentDescription: null,
  address: {
    rawInput: null,
    formattedAddress: null,
    precision: AddressPrecision.Approximate
  } as AddressDto,
  deviceVendor: null,
  localLatitude: null,
  localLongitude: null,
  extractedMetadataHints: { dateTime: false, location: false, description: false },
  metadataWarning: { dateTime: false, location: false },

  // --- The following are static on submit. ---

  // Moderators will determine if the incident requires a content warning based on the media and details provided by the user.
  hasContentWarning: false,

  // The user must confirm the disclaimer before proceeding with the report submission.
  isFilmedInPublicLocation: true,
  userConsentProvided: true
})

export const useIncidentSubmissionStore = defineStore('incidentSubmission', () => {
  const { creatorIncidentReportsApi } = useApi()

  const step = ref<StepName>('start')
  const maxStepIndex = ref<number>(0)
  const isSubmitting = ref<boolean>(false)

  const incidentForm: LocalIncidentReportState = reactive(initReportDetailsForm())

  const activeStepOrder = computed<StepName[]>(() => {
    const steps: StepName[] = ['start']
    if (getExtractor(incidentForm.deviceVendor).supportsDirectoryBrowse) {
      steps.push('vendor-browse')
    }
    steps.push('media-upload', 'incident-details', 'refine-location', 'review-submit')
    return steps
  })

  const currentStepIndex = computed(() => activeStepOrder.value.indexOf(step.value))
  const isLastStep = computed(() => step.value === 'review-submit')

  function updateGeoJsonLocation() {
    if (incidentForm.localLongitude !== null && incidentForm.localLatitude !== null) {
      incidentForm.location = {
        type: 'Point',
        coordinates: [incidentForm.localLongitude, incidentForm.localLatitude]
      } as Geometry & { coordinates: number[] }
    } else {
      incidentForm.location = null
    }
  }

  function $reset() {
    step.value = 'start'
    maxStepIndex.value = 0
    isSubmitting.value = false
    Object.assign(incidentForm, initReportDetailsForm())
  }

  // Start the wizard from what a bounty asks for. Real file metadata may overwrite the time and location later.
  function prefillFromBounty(bounty: BountyDto) {
    const coordinates = bounty.location?.coordinates
    if (coordinates && coordinates.length >= 2) {
      incidentForm.localLongitude = coordinates[0]
      incidentForm.localLatitude = coordinates[1]
      updateGeoJsonLocation()
    }
    if (bounty.address) incidentForm.address = { ...incidentForm.address, ...bounty.address }
    if (bounty.windowStartDateTime) incidentForm.incidentDateTime = bounty.windowStartDateTime
    if (bounty.windowTimezone) incidentForm.incidentTimezone = bounty.windowTimezone
    if (bounty.incidentReportType) incidentForm.incidentReportType = bounty.incidentReportType
  }

  function nextStep() {
    const order = activeStepOrder.value
    const nextIdx = currentStepIndex.value + 1
    if (nextIdx < order.length) {
      step.value = order[nextIdx]
      if (nextIdx > maxStepIndex.value) {
        maxStepIndex.value = nextIdx
      }
    }
  }

  function previousStep() {
    const order = activeStepOrder.value
    const prevIdx = currentStepIndex.value - 1
    if (prevIdx >= 0) {
      step.value = order[prevIdx]
    }
  }

  function jumpToStep(targetStep: StepName) {
    const order = activeStepOrder.value
    const targetIdx = order.indexOf(targetStep)
    if (targetIdx !== -1 && targetIdx <= maxStepIndex.value) {
      step.value = targetStep
    }
  }

  function isStepDone(stepName: StepName): boolean {
    return activeStepOrder.value.indexOf(stepName) < maxStepIndex.value
  }

  // Resolves to the created report's id, or null when the submit failed.
  async function submitReport(): Promise<string | null> {
    if (isSubmitting.value) return null
    isSubmitting.value = true

    // 1. Data Preparation (Outside the try/catch)
    const fileUploadStore = useFileUploadStore()
    const validFiles = fileUploadStore.userFileList.filter(
      (f) => f.isUploaded && !f.hasError && f.id
    )
    const validFileIds = validFiles.map((f) => f.id as string)

    const primary = validFiles.find((f) => f.isPrimary)
    if (primary && primary.id) {
      incidentForm.primaryUserIncidentFileId = primary.id
    }

    const payload = {
      incidentReportDetails: incidentForm,
      userFileIds: validFileIds
    }

    // 2. Focused Exception Handling (Only the API call)
    try {
      const created = await creatorIncidentReportsApi.creatorIncidentReportsSubmitReport({
        submitIncidentReportRequest: payload
      })
      return created.id ?? null
    } catch (error) {
      console.error('Store: Failed to submit report', error)
      return null
    } finally {
      // Always release the loading state whether it succeeds or fails
      isSubmitting.value = false
    }
  }

  return {
    $reset,
    step,
    maxStepIndex,
    isSubmitting,
    activeStepOrder,
    currentStepIndex,
    isLastStep,
    nextStep,
    previousStep,
    jumpToStep,
    isStepDone,
    incidentForm,
    updateGeoJsonLocation,
    prefillFromBounty,
    submitReport
  }
})
